// conflictAnalysis.ts
import { AccountEntry } from '../types';

export interface DataConflict {
  accountNumber: string;
  accountName: string;
  month: string;
  existingDebit: number;
  existingCredit: number;
  newDebit: number;
  newCredit: number;
  existingValue: number;
  newValue: number;
  percentChange: number;
  conflictType: 'value_change' | 'new_period' | 'new_account';
}

export interface ConflictAnalysisResult {
  conflicts: DataConflict[];
  newAccounts: { accountNumber: string; accountName: string; months: string[] }[];
  newPeriods: string[];
  totalConflicts: number;
  summary: {
    accountsAffected: number;
    periodsAffected: number;
    significantChanges: number; // > 10% change
  };
}

/**
 * Analyzes conflicts between existing data and new data being uploaded
 */
export function analyzeDataConflicts(
  existingData: AccountEntry[],
  newData: AccountEntry[]
): ConflictAnalysisResult {
  const conflicts: DataConflict[] = [];
  const newAccounts: { accountNumber: string; accountName: string; months: string[] }[] = [];
  const newPeriods = new Set<string>();
  
  // Create lookup maps for existing data
  const existingAccountMap = new Map<string, AccountEntry[]>();
  const existingAccountsByMonth = new Map<string, Map<string, AccountEntry>>();
  
  // Index existing data
  existingData.forEach(entry => {
    const key = `${entry.accountNumber}-${entry.month}`;
    
    // Group by account
    if (!existingAccountMap.has(entry.accountNumber)) {
      existingAccountMap.set(entry.accountNumber, []);
    }
    existingAccountMap.get(entry.accountNumber)!.push(entry);
    
    // Group by month
    if (!existingAccountsByMonth.has(entry.month)) {
      existingAccountsByMonth.set(entry.month, new Map());
    }
    existingAccountsByMonth.get(entry.month)!.set(entry.accountNumber, entry);
  });
  
  // Track all existing periods and accounts
  const existingPeriods = new Set(existingData.map(e => e.month));
  const existingAccountNumbers = new Set(existingData.map(e => e.accountNumber));
  
  // Analyze each new data entry
  newData.forEach(newEntry => {
    const isNewAccount = !existingAccountNumbers.has(newEntry.accountNumber);
    const isNewPeriod = !existingPeriods.has(newEntry.month);
    
    if (isNewPeriod) {
      newPeriods.add(newEntry.month);
    }
    
    // ENHANCED: Check for account name similarity to detect synthetic account number conflicts
    const existingAccountsWithSameNumber = existingData.filter(e => e.accountNumber === newEntry.accountNumber);
    const hasSameAccountName = existingAccountsWithSameNumber.some(e => 
      e.name.toLowerCase().trim() === newEntry.name.toLowerCase().trim()
    );
    
    // If account number exists but account name is different, this might be a synthetic numbering conflict
    const isSyntheticConflict = existingAccountsWithSameNumber.length > 0 && !hasSameAccountName;
    
    if (isNewAccount) {
      // Find if we've already recorded this new account
      let existingNewAccount = newAccounts.find(na => na.accountNumber === newEntry.accountNumber);
      if (!existingNewAccount) {
        existingNewAccount = {
          accountNumber: newEntry.accountNumber,
          accountName: newEntry.name,
          months: []
        };
        newAccounts.push(existingNewAccount);
      }
      if (!existingNewAccount.months.includes(newEntry.month)) {
        existingNewAccount.months.push(newEntry.month);
      }
    }
    
    // Check for conflicts with existing data
    const existingEntry = existingAccountsByMonth.get(newEntry.month)?.get(newEntry.accountNumber);
    
    if (existingEntry) {
      // ENHANCED: Only create conflict if it's the same logical account (same name)
      const isSameLogicalAccount = existingEntry.name.toLowerCase().trim() === newEntry.name.toLowerCase().trim();
      
      if (isSameLogicalAccount) {
        // Calculate net values for comparison
        const existingValue = existingEntry.debit - existingEntry.credit;
        const newValue = newEntry.debit - newEntry.credit;
        
        // Only create conflict if values are actually different
        if (Math.abs(existingValue - newValue) > 0.01) {
          const percentChange = existingValue !== 0 
            ? ((newValue - existingValue) / Math.abs(existingValue)) * 100
            : (newValue !== 0 ? 100 : 0);
          
          conflicts.push({
            accountNumber: newEntry.accountNumber,
            accountName: newEntry.name,
            month: newEntry.month,
            existingDebit: existingEntry.debit,
            existingCredit: existingEntry.credit,
            newDebit: newEntry.debit,
            newCredit: newEntry.credit,
            existingValue,
            newValue,
            percentChange,
            conflictType: 'value_change'
          });
        }
      } else {
        // Different account names with same number - treat as new account with note
        console.log(`[conflictAnalysis] Account number conflict detected: ${newEntry.accountNumber} - "${existingEntry.name}" vs "${newEntry.name}". Treating as new account.`);
        
        conflicts.push({
          accountNumber: newEntry.accountNumber,
          accountName: newEntry.name,
          month: newEntry.month,
          existingDebit: 0,
          existingCredit: 0,
          newDebit: newEntry.debit,
          newCredit: newEntry.credit,
          existingValue: 0,
          newValue: newEntry.debit - newEntry.credit,
          percentChange: 100,
          conflictType: 'new_account'
        });
      }
    } else if (!isNewAccount && isNewPeriod) {
      // Existing account but new period
      conflicts.push({
        accountNumber: newEntry.accountNumber,
        accountName: newEntry.name,
        month: newEntry.month,
        existingDebit: 0,
        existingCredit: 0,
        newDebit: newEntry.debit,
        newCredit: newEntry.credit,
        existingValue: 0,
        newValue: newEntry.debit - newEntry.credit,
        percentChange: 100,
        conflictType: 'new_period'
      });
    } else if (isNewAccount) {
      // New account
      conflicts.push({
        accountNumber: newEntry.accountNumber,
        accountName: newEntry.name,
        month: newEntry.month,
        existingDebit: 0,
        existingCredit: 0,
        newDebit: newEntry.debit,
        newCredit: newEntry.credit,
        existingValue: 0,
        newValue: newEntry.debit - newEntry.credit,
        percentChange: 100,
        conflictType: 'new_account'
      });
    }
  });
  
  // Calculate summary statistics
  const accountsAffected = new Set(conflicts.map(c => c.accountNumber)).size;
  const periodsAffected = new Set(conflicts.map(c => c.month)).size;
  const significantChanges = conflicts.filter(c => 
    c.conflictType === 'value_change' && Math.abs(c.percentChange) > 10
  ).length;
  
  return {
    conflicts,
    newAccounts,
    newPeriods: Array.from(newPeriods).sort(),
    totalConflicts: conflicts.length,
    summary: {
      accountsAffected,
      periodsAffected,
      significantChanges
    }
  };
}

/**
 * Applies smart merge strategy preserving existing categorizations
 */
/**
 * Applies smart merge strategy preserving existing categorizations
 */
// CORRECTED VERSION - Preserve order, just fix categories
// ENHANCED VERSION - Insert entries in proper chronological position
export function mergeDataWithConflictResolution(
  existingData: AccountEntry[],
  newData: AccountEntry[],
  conflictResolutions: { [key: string]: 'keep_existing' | 'use_new' } = {}
): AccountEntry[] {
  console.log('[mergeDataWithConflictResolution] Starting merge - inserting in chronological order');

  // Create lookup for existing categories by account number
  const existingCategoriesByAccount = new Map<string, string>();
  existingData.forEach(entry => {
    if (entry.category && entry.category !== 'N/A') {
      existingCategoriesByAccount.set(entry.accountNumber, entry.category);
    }
  });
  
  // Create lookup map for existing entries
  const existingLookup = new Map<string, number>();
  existingData.forEach((entry, index) => {
    const key = `${entry.accountNumber}-${entry.month}`;
    existingLookup.set(key, index);
  });
  
  // Start with a copy of existing data
  let result: AccountEntry[] = [...existingData];
  
  // Helper function to compare dates
  const compareDates = (dateA: string, dateB: string): number => {
    return new Date(dateA).getTime() - new Date(dateB).getTime();
  };
  
  // Group new data by account number
  const newDataByAccount = new Map<string, AccountEntry[]>();
  const conflictUpdates: { index: number; entry: AccountEntry }[] = [];
  
  newData.forEach(newEntry => {
    const key = `${newEntry.accountNumber}-${newEntry.month}`;
    const existingIndex = existingLookup.get(key);
    
    if (existingIndex !== undefined) {
      // Handle conflicts for existing entries
      const resolution = conflictResolutions[key] || 'use_new';
      
      if (resolution === 'use_new') {
        const existingEntry = result[existingIndex];
        conflictUpdates.push({
          index: existingIndex,
          entry: {
            ...newEntry,
            category: existingEntry.category || newEntry.category,
            type: existingEntry.type || newEntry.type
          }
        });
      }
    } else {
      // Collect new entries by account for insertion
      if (!newDataByAccount.has(newEntry.accountNumber)) {
        newDataByAccount.set(newEntry.accountNumber, []);
      }
      
      // Add category from existing data for same account
      const existingCategory = existingCategoriesByAccount.get(newEntry.accountNumber);
      const entryToAdd = {
        ...newEntry,
        category: existingCategory || newEntry.category || 'N/A',
        type: newEntry.type || 'n/a'
      };
      
      newDataByAccount.get(newEntry.accountNumber)!.push(entryToAdd);
    }
  });
  
  // Apply conflict updates first
  conflictUpdates.forEach(({ index, entry }) => {
    result[index] = entry;
  });
  
  // Insert new entries in chronological order for each account
  newDataByAccount.forEach((newEntries, accountNumber) => {
    console.log(`[mergeDataWithConflictResolution] Processing ${newEntries.length} new entries for account ${accountNumber}`);
    
    // Sort new entries by date
    newEntries.sort((a, b) => compareDates(a.date, b.date));
    
    // Find all existing entries for this account with their positions
    const accountEntries: { entry: AccountEntry; originalIndex: number }[] = [];
    result.forEach((entry, index) => {
      if (entry.accountNumber === accountNumber) {
        accountEntries.push({ entry, originalIndex: index });
      }
    });
    
    // Process each new entry to find its correct insertion point
    newEntries.forEach(newEntry => {
      let insertionIndex = -1;
      
      // Find the correct position based on date
      for (let i = 0; i < accountEntries.length; i++) {
        const existingEntry = accountEntries[i];
        
        // If new entry date is before this existing entry, insert here
        if (compareDates(newEntry.date, existingEntry.entry.date) < 0) {
          insertionIndex = existingEntry.originalIndex;
          break;
        }
      }
      
      if (insertionIndex === -1) {
        // New entry is after all existing entries for this account
        if (accountEntries.length > 0) {
          // Insert after the last entry for this account
          const lastEntry = accountEntries[accountEntries.length - 1];
          insertionIndex = lastEntry.originalIndex + 1;
        } else {
          // No existing entries for this account, append at end
          insertionIndex = result.length;
        }
      }
      
      console.log(`[mergeDataWithConflictResolution] Inserting ${accountNumber} ${newEntry.month} at position ${insertionIndex}`);
      
      // Insert the entry
      result.splice(insertionIndex, 0, newEntry);
      
      // Update indices for subsequent operations
      accountEntries.forEach(item => {
        if (item.originalIndex >= insertionIndex) {
          item.originalIndex++;
        }
      });
    });
  });
  
  console.log('[mergeDataWithConflictResolution] Merge completed - entries inserted chronologically:', {
    originalCount: existingData.length,
    finalCount: result.length
  });
  
  return result;
}

/**
 * Formats currency values for display
 */
export function formatConflictValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Gets a color class based on conflict severity
 */
export function getConflictSeverityColor(conflict: DataConflict): string {
  if (conflict.conflictType === 'new_account' || conflict.conflictType === 'new_period') {
    return 'text-blue-600'; // New items are neutral
  }
  
  const absChange = Math.abs(conflict.percentChange);
  if (absChange > 50) return 'text-red-600'; // Major change
  if (absChange > 10) return 'text-orange-600'; // Moderate change
  return 'text-yellow-600'; // Minor change
}

/**
 * Gets a human-readable description of the conflict
 */
export function getConflictDescription(conflict: DataConflict): string {
  switch (conflict.conflictType) {
    case 'new_account':
      return `New account "${conflict.accountName}" will be added`;
    case 'new_period':
      return `New period data for existing account "${conflict.accountName}"`;
    case 'value_change':
      const direction = conflict.newValue > conflict.existingValue ? 'increase' : 'decrease';
      const change = Math.abs(conflict.percentChange);
      if (change > 100) {
        return `Large ${direction} of ${change.toFixed(0)}%`;
      } else if (change > 10) {
        return `${direction.charAt(0).toUpperCase() + direction.slice(1)} of ${change.toFixed(1)}%`;
      } else {
        return `Minor ${direction} of ${change.toFixed(1)}%`;
      }
    default:
      return 'Data will be updated';
  }
}